import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  FormControl,
  InputLabel,
  MenuItem,
  Button,
  TextField,
  CardActions,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import axios from "../../utils/axios";
import HomeIcon from "@mui/icons-material/Home";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { CircularIndeterminate } from "../components/Progress";
import {
  MultipleSelectForFormik,
  SingleSelectForFormik,
} from "../../utils/form";

const Card = styled(MuiCard)`
  ${spacing};

  box-shadow: none;
`;

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

function SupportUserNew() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [options, setOptions] = useState({
    role: [],
    status: [],
  });
  const [games, setGames] = useState([]);

  const getPayment = useCallback(async () => {
    try {
      setLoading(true);

      const { data: options } = await axios.options(`/api/v1/support-users`);
      setOptions(options);

      const { data: games } = await axios.get(`/api/v1/games`);
      setGames(games.entries);

      setLoading(false);
    } catch (e) {
      setError(e.message);
      console.log(e);
    }
  }, [setLoading, setError, setOptions, setGames]);

  useEffect(() => {
    getPayment();
  }, [getPayment]);

  if (loading) {
    return <CircularIndeterminate />;
  }

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title="Support User New" />

      <Typography variant="h3" gutterBottom display="inline">
        Create New User
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/support-users">
          Support Users
        </Link>
        <Typography>Create New User</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Formik
        initialValues={{
          name: "",
          email: "",
          role: "",
          games: [],
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string().max(255).required("Name is required"),
          email: Yup.string()
            .email("Must be a valid email")
            .max(255)
            .required("Email is required"),
          role: Yup.string().required("Role is required"),
          games: Yup.array().required("At least one game is required"),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            const { data } = await axios.post(`/api/v1/support-users`, values);

            navigate(`/support-users/${data.id}`);
          } catch (error) {
            console.log(error);
            const message = error.messages || "Something went wrong";

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Grid container spacing={4}>
                  {errors.submit && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{errors.submit}</Alert>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      type="text"
                      name="name"
                      label="Name"
                      value={values.name}
                      error={Boolean(touched.name && errors.name)}
                      fullWidth
                      helperText={touched.name && errors.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      type="email"
                      name="email"
                      label="Email Address"
                      value={values.email}
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="role-label">Role</InputLabel>
                      <Field
                        label="Role"
                        labelId="role-label"
                        id="role"
                        name="role"
                        component={SingleSelectForFormik}
                      >
                        {options.role.map(({ value, display_name }) => (
                          <MenuItem value={value} key={value}>
                            {display_name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel id="games-label">Games</InputLabel>
                      <Field
                        label="Games"
                        labelId="games-label"
                        id="games"
                        name="games"
                        component={MultipleSelectForFormik}
                      >
                        {games.map(({ id, name }) => (
                          <MenuItem value={id} key={id}>
                            {name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  Create User
                </Button>
                <Button
                  color="primary"
                  onClick={() => navigate("/support-users")}
                >
                  Cancel
                </Button>
              </CardActions>
            </Card>
          </form>
        )}
      </Formik>
    </React.Fragment>
  );
}

export default SupportUserNew;
